import { DialogComponent } from "@theme/dialog";

const STORAGE_KEY = "wistearia:newsletter";
const STARTED_AT_KEY = "wistearia:newsletter:started-at";
const ADD_TO_CART_BAR_SELECTOR = ".sticky-add-to-cart__bar";
const TEASER_DOCK_SELECTOR = "[data-newsletter-teaser-dock]";
const MOTION_READY_ATTRIBUTE = "data-motion-ready";
/** Room kept around the teaser: any closer and it steps aside for the Add to cart bar or the footer's content. */
const CLEARANCE_PX = 8;
/** The footer's content besides its words: images, icons and controls. */
const FOOTER_MEDIA_SELECTOR = "img, svg, video, iframe, input, select, textarea, button";

/** Reset an existing countdown on a new document, even where no popup is rendered. */
export function resetNewsletterTimerOnNavigation() {
  const navigation = /** @type {PerformanceNavigationTiming | undefined} */ (
    performance.getEntriesByType("navigation")[0]
  );
  if (globalThis.Shopify?.designMode || (navigation?.type !== "reload" && navigation?.type !== "back_forward")) {
    return;
  }
  try {
    if (sessionStorage.getItem(STARTED_AT_KEY) !== null) {
      sessionStorage.setItem(STARTED_AT_KEY, String(Date.now()));
    }
  } catch {
    // The popup can still use its current-page delay when browser storage is restricted.
  }
}

// This module loads on every storefront page. Reset before custom-element upgrade
// starts a timer; ordinary navigation and bfcache restores keep their countdown.
resetNewsletterTimerOnNavigation();

/** A single, dismissible invitation using the existing native signup and dialog. */
class NewsletterPopup extends DialogComponent {
  #abortController = new AbortController();
  /** @type {number | undefined} */
  #timer;
  #subscribed = false;
  #automaticOpeningPending = false;
  /** @type {IntersectionObserver | undefined} */
  #footerObserver;
  /** @type {ResizeObserver | undefined} */
  #overlapObserver;
  /** @type {number | undefined} */
  #motionFrame;

  #tryOpen = () => {
    if (this.#subscribed || !this.isConnected) {
      return;
    }
    // Never interrupt typing, search, cart, navigation, or a hidden browser tab.
    if (this.#shouldDeferOpening()) {
      this.#timer = globalThis.setTimeout(this.#tryOpen, 5000);
      return;
    }
    this.#automaticOpeningPending = true;
    this.showDialog();
  };

  #onClose = () => {
    this.#automaticOpeningPending = false;
    if (!this.#subscribed && !globalThis.Shopify?.designMode) {
      this.#writeState("dismissed");
    }
    this.#setTeaser(!this.#subscribed);
  };

  #onOpen = () => {
    this.#automaticOpeningPending = false;
    const feedback = this.querySelector(
      "[aria-invalid='true'], .email-signup-block--confirmed [data-email-signup-confirmation]",
    );
    if (feedback instanceof HTMLElement) {
      if (Object.hasOwn(feedback.dataset, "emailSignupConfirmation")) {
        feedback.setAttribute("tabindex", "-1");
      }
      feedback.focus({ preventScroll: true });
      // Reveal feedback in short dialogs without scrolling the locked background.
      const { dialog } = this.refs;
      const visibleTop = dialog.getBoundingClientRect().top + dialog.clientTop;
      const visibleBottom = visibleTop + dialog.clientHeight;
      const bounds = feedback.getBoundingClientRect();
      if (bounds.top < visibleTop || bounds.height > dialog.clientHeight) {
        dialog.scrollBy({ top: bounds.top - visibleTop, behavior: "instant" });
      } else if (bounds.bottom > visibleBottom) {
        dialog.scrollBy({ top: bounds.bottom - visibleBottom, behavior: "instant" });
      }
    } else {
      // Focus the named dialog without selecting a control or opening a mobile keyboard.
      this.refs.dialog.focus({ preventScroll: true });
    }
  };

  /** @param {Event} event */
  #onEditorSelect = event => {
    if (event.target instanceof Element && event.target.contains(this)) {
      this.showDialog();
    }
  };

  /** @param {Event} event */
  #onEditorDeselect = event => {
    if (event.target instanceof Element && event.target.contains(this)) {
      this.closeDialog();
    }
  };

  /** @param {Event} event */
  #onConfirmation = event => {
    if (event instanceof CustomEvent && event.detail?.block instanceof Element) {
      this.#confirm(event.detail.block);
    }
  };

  /** @param {StorageEvent} event */
  #onStorage = event => {
    if (event.key !== STORAGE_KEY || !event.newValue) {
      return;
    }
    this.#syncStoredState();
  };

  /** @param {PageTransitionEvent} event */
  #onPageShow = event => {
    if (!event.persisted) {
      return;
    }
    this.#syncStoredState();
    if (!this.#readState() && !this.#subscribed && !this.refs.dialog.open && !globalThis.Shopify?.designMode) {
      this.#scheduleOpening();
    }
  };

  // A bfcache restoration doesn't reconnect the element or send a storage event.
  #syncStoredState = () => {
    const state = this.#readState();
    if (state !== "dismissed" && state !== "subscribed") {
      return;
    }
    const cancelAutomaticOpening = this.#automaticOpeningPending;
    this.#automaticOpeningPending = false;
    clearTimeout(this.#timer);
    this.#subscribed = this.#subscribed || state === "subscribed";
    this.#setTeaser(!this.#subscribed);
    if (this.#subscribed || cancelAutomaticOpening) {
      this.closeDialog();
    }
  };

  #shouldDeferOpening() {
    return Boolean(
      document.hidden ||
      document.querySelector("dialog[open], details[open][scroll-lock], .email-signup-block form[aria-busy='true']") ||
      this.#hasVisiblePrivacyControls() ||
      document.documentElement.hasAttribute("scroll-lock") ||
      document.body.style.position === "fixed" ||
      this.#hasEditingFocus(),
    );
  }

  #hasEditingFocus() {
    const activeElement = document.activeElement;
    return Boolean(
      activeElement?.closest(".email-signup-block") ||
      activeElement?.matches("input, textarea, select, [contenteditable]:not([contenteditable='false'])"),
    );
  }

  #hasVisiblePrivacyControls() {
    // Shopify's div controls can remain mounted beneath a hidden parent after close.
    return [...document.querySelectorAll("#shopify-pc__banner, #shopify-pc__prefs__dialog")].some(control => {
      if (!(control instanceof HTMLElement) || control.closest('[hidden], [aria-hidden="true"], [inert]')) {
        return false;
      }
      const visible =
        typeof control.checkVisibility === "function"
          ? control.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true })
          : getComputedStyle(control).visibility !== "hidden";
      return visible && control.getClientRects().length > 0;
    });
  }

  /** Recheck at the opening frame, including state changes that emit no focus event. */
  canOpenDialog() {
    if (!this.#automaticOpeningPending) {
      return true;
    }
    this.#automaticOpeningPending = false;
    const state = this.#readState();
    if (state === "dismissed" || state === "subscribed") {
      this.#syncStoredState();
      return false;
    }
    if (this.#subscribed || !this.isConnected) {
      return false;
    }
    if (this.#shouldDeferOpening()) {
      this.#timer = globalThis.setTimeout(this.#tryOpen, 5000);
      return false;
    }
    return true;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const state = this.#readState();
    this.#subscribed = this.dataset.subscribed === "true" || state === "subscribed";
    this.addEventListener("dialog:close", this.#onClose, { signal });
    this.addEventListener("dialog:open", this.#onOpen, { signal });
    document.addEventListener("email-signup:confirmed", this.#onConfirmation, { signal });
    globalThis.addEventListener("storage", this.#onStorage, { signal });
    globalThis.addEventListener("pageshow", this.#onPageShow, { signal });
    if (globalThis.Shopify?.designMode) {
      document.addEventListener("shopify:section:select", this.#onEditorSelect, { signal });
      document.addEventListener("shopify:section:deselect", this.#onEditorDeselect, { signal });
    }

    this.#watchFooter();
    this.#watchOverlaps();

    // Confirmation can run before this module upgrades, and clears the URL flag.
    const confirmation = document.querySelector(".email-signup-block--confirmed");
    if (confirmation) {
      this.#confirm(confirmation);
    } else if (this.querySelector(".email-signup__message--error")) {
      this.showDialog();
    } else if (!this.#subscribed && !globalThis.Shopify?.designMode) {
      if (state === "dismissed") {
        this.#setTeaser(true);
      } else {
        this.#scheduleOpening();
      }
    }
  }

  disconnectedCallback() {
    this.#automaticOpeningPending = false;
    clearTimeout(this.#timer);
    this.#abortController.abort();
    this.#footerObserver?.disconnect();
    this.#overlapObserver?.disconnect();
    cancelAnimationFrame(this.#motionFrame ?? 0);
    // Also cancels the base dialog's pending animation frame before it can lock scroll.
    this.closeDialog();
    super.disconnectedCallback();
  }

  /** The teaser uses the same focus/scroll lifecycle as the automatic invitation. */
  openPopup() {
    if (this.#subscribed) {
      return;
    }
    clearTimeout(this.#timer);
    this.#automaticOpeningPending = false;
    this.showDialog();
  }

  #scheduleOpening() {
    const now = Date.now();
    let startedAt = now;
    try {
      const stored = Number(sessionStorage.getItem(STARTED_AT_KEY));
      if (Number.isFinite(stored) && stored > 0 && stored <= now) {
        startedAt = stored;
      } else {
        sessionStorage.setItem(STARTED_AT_KEY, String(now));
      }
    } catch {
      // Restricted storage still gets a working timer for the current page.
    }
    clearTimeout(this.#timer);
    const remaining = Math.max(0, startedAt + Number(this.dataset.delay || 20) * 1000 - now);
    this.#timer = globalThis.setTimeout(this.#tryOpen, remaining);
  }

  /** @param {Element} block */
  #confirm(block) {
    this.#automaticOpeningPending = false;
    this.#subscribed = true;
    this.#writeState("subscribed");
    clearTimeout(this.#timer);
    this.#setTeaser(false);
    if (this.contains(block)) {
      // Keep the signup protected from accidental backdrop clicks until it succeeds.
      this.removeAttribute("disable-backdrop-close");
      this.showDialog();
    } else {
      // An inline signup also cancels any opening already queued for the next frame.
      this.closeDialog();
    }
  }

  /**
   * Marks the teaser's dock while the page footer is on screen, where the teaser would cover its links. The first state
   * is set at once and eases only from the next frames on, so a teaser that starts over the footer never flashes past.
   */
  #watchFooter() {
    const dock = this.querySelector(TEASER_DOCK_SELECTOR);
    if (!(dock instanceof HTMLElement)) {
      return;
    }
    const footer = this.closest("footer");
    if (footer) {
      const { top, bottom } = footer.getBoundingClientRect();
      dock.toggleAttribute("data-footer-in-view", top < globalThis.innerHeight && bottom > 0);
      this.#footerObserver = new IntersectionObserver(entries => {
        const entry = entries.at(-1);
        if (entry) {
          dock.toggleAttribute("data-footer-in-view", entry.isIntersecting);
        }
      });
      this.#footerObserver.observe(footer);
    }
    this.#armMotion(dock);
  }

  /**
   * Lets the dock ease between its places from the next frames on, not from the state it was just put in.
   * @param {HTMLElement} dock
   */
  #armMotion(dock) {
    dock.toggleAttribute(MOTION_READY_ATTRIBUTE, false);
    cancelAnimationFrame(this.#motionFrame ?? 0);
    this.#motionFrame = requestAnimationFrame(() => {
      this.#motionFrame = requestAnimationFrame(() => dock.toggleAttribute(MOTION_READY_ATTRIBUTE, true));
    });
  }

  /**
   * Marks the teaser's dock where something at the foot of the window would reach the teaser's column: the Add to cart
   * bar, centred there on phones and narrower windows (wider ones keep the teaser beside it), and the footer's words,
   * links, fields and images, which scroll up under it. On a wide window the footer's content starts inside the page
   * margin, clear of the teaser, which then stays. Measured again when the window, the teaser, the bar or the footer
   * resizes.
   */
  #watchOverlaps() {
    const dock = this.querySelector(TEASER_DOCK_SELECTOR);
    if (!(dock instanceof HTMLElement)) {
      return;
    }
    const measure = () => this.#measureOverlaps(dock);
    this.#overlapObserver = new ResizeObserver(measure);
    this.#overlapObserver.observe(dock);
    // The bar has no box while a sold-out variant hides it, so it is watched too and measured once it shows. Variant
    // changes morph its contents and keep the element.
    const bar = document.querySelector(ADD_TO_CART_BAR_SELECTOR);
    if (bar) {
      this.#overlapObserver.observe(bar);
    }
    const footer = this.closest("footer");
    if (footer) {
      this.#overlapObserver.observe(footer);
    }
    globalThis.addEventListener("resize", measure, { signal: this.#abortController.signal });
  }

  /** @param {HTMLElement} dock */
  #measureOverlaps(dock) {
    const teaser = dock.getBoundingClientRect();
    const left = teaser.left - CLEARANCE_PX;
    const right = teaser.right + CLEARANCE_PX;
    /** @param {Iterable<DOMRect>} rects */
    const reach = rects => teaser.width > 0 && [...rects].some(rect => rect.left < right && rect.right > left);
    const bar = document.querySelector(ADD_TO_CART_BAR_SELECTOR)?.getBoundingClientRect();
    dock.toggleAttribute("data-overlaps-bar", Boolean(bar && bar.width > 0 && reach([bar])));
    const footer = this.closest("footer");
    dock.toggleAttribute(
      "data-covers-footer",
      Boolean(footer && teaser.width > 0 && this.#footerReaches(footer, reach)),
    );
  }

  /**
   * Whether any of the footer's words, images, icons or controls, outside this pop-up, is in the teaser's column.
   * Visually hidden labels, a pixel wide, are not.
   * @param {Element} footer @param {(rects: Iterable<DOMRect>) => boolean} reach
   */
  #footerReaches(footer, reach) {
    const range = document.createRange();
    const walker = document.createTreeWalker(footer, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
      acceptNode: node => (node === this ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
    });
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      /** @type {Iterable<DOMRect>} */
      let rects = [];
      if (node instanceof Text && node.data.trim() && (node.parentElement?.getBoundingClientRect().width ?? 0) > 1) {
        range.selectNodeContents(node);
        rects = range.getClientRects();
      } else if (node instanceof Element && node.matches(FOOTER_MEDIA_SELECTOR)) {
        rects = [...node.getClientRects()].filter(rect => rect.width > 1 && rect.height > 1);
      }
      if (reach(rects)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Shows the teaser already where it belongs, stepped aside if the bar or the footer's content is there, instead of
   * easing out of the way from view.
   * @param {boolean} visible
   */
  #setTeaser(visible) {
    const teaser = this.querySelector("[data-newsletter-teaser]");
    if (!(teaser instanceof HTMLElement) || teaser.hidden !== visible) {
      return;
    }
    const dock = teaser.closest(TEASER_DOCK_SELECTOR);
    if (visible && dock instanceof HTMLElement) {
      dock.toggleAttribute(MOTION_READY_ATTRIBUTE, false);
      teaser.hidden = false;
      this.#measureOverlaps(dock);
      this.#armMotion(dock);
    } else {
      teaser.hidden = !visible;
    }
  }

  #readState() {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }

  /** @param {string} state */
  #writeState(state) {
    try {
      localStorage.setItem(STORAGE_KEY, state);
    } catch {
      // The current-page dismissal still works when browser storage is restricted.
    }
  }
}

if (!customElements.get("newsletter-popup")) {
  customElements.define("newsletter-popup", NewsletterPopup);
}
